import { AbstractControl } from '@angular/forms';
import { YesNo } from '../../../shared/model/yes-no.model';
import { SimpleLookupResult } from '../../../type-definitions/general/general';
import { ControlType } from './control-type.model';
import { IInputMaskConfig } from './input-mask-config.model';
import { OfficeToolsEvents } from './office-tools-events.model';

export interface OfficeDataItemBase {
  KeyID: number;
  KeyStyleViewpointVistaDataHasAttachments: boolean;
  UniqueAttchID: string;
}

export type OfficeFieldsValuesObj<T = unknown> = Record<number, T>;
export type OfficeDataItem = OfficeFieldsValuesObj & OfficeDataItemBase;

export interface IOfficeAvailableLookup {
  Lookup: string;
  LoadSeq: number;
  DependentFields: number[];
  IsStatic: boolean;
  Data: unknown;
  Title: string;
  Active: boolean;
}

export interface IOfficeVistaFormItem {
  ActiveLookup: YesNo;
  AutoSeqType: number;
  ColumnName: string;
  ComboType: string;
  Computed: YesNo;
  Datatype: string;
  Description: string;
  FieldType: number;
  GridCol: number;
  GridColHeading: string;
  InputLength: number;
  InputSeq: number;
  InputType: number;
  Label: unknown;
  LookupParams: string;
  MaxValue: string;
  MinValue: string;
  Req: YesNo;
  SecondaryValLevel: number;
  SecondaryValParams: unknown;
  SecondaryValProc: unknown;
  Seq: number;
  ShowForm: YesNo;
  ShowGrid: YesNo;
  ValExpError: unknown;
  ValExpression: unknown;
  ValLevel: number;
  ValParams: string;
  ValProc: string;
  ViewName: string;
  ControlType: ControlType;
}

export enum OfficeFieldWebInputTypeEnum {
  string = 'string',
  web = 'web',
  email = 'email',
  byte = 'byte',
  short = 'short',
  int = 'int',
  double = 'double',
  long = 'long',
  checkbox = 'checkbox',
  notes = 'notes',
  combobox = 'combobox',
  richtext = 'richtext',
  date = 'date',
  month = 'month',
  time = 'time',
  percent = 'percent',
  radiobutton = 'radiobutton',
  file = 'file',
}

export type OfficeFieldWebInputType = `${OfficeFieldWebInputTypeEnum}`;

interface IFilterConfiguration {
  FilteredSeq: number;
}

export enum OfficeFieldValLevelEnum {
  NoValidation = 0,
}

export interface IOfficeField {
  AutoSeqType: string;
  AvailableLookups: IOfficeAvailableLookup[];
  DefaultValue: unknown;
  DefaultLookup: string;
  DefaultsOtherFields: boolean;
  fieldFormAddModeConfig?: IFieldFormModeConfig;
  GridLabel: string;
  GroupID: number;
  InputLength: number;
  InputMask: string;
  InputMaskConfig?: IInputMaskConfig;
  InputType: number;
  IsReadOnly: boolean;
  ItemGroupSeq: number;
  KeySeq: number;
  Prec: number;
  RecordLabel: string;
  LabelDescriptions: ILabelDescription[];
  Seq: number;
  Tab: number;
  ValLevel: number;
  VistaFormItem: IOfficeVistaFormItem;
  WebInputType: OfficeFieldWebInputType;
  FilterConfiguration: IFilterConfiguration;
  FieldLabelLocalization?: IFieldLabelLocalization;
}

export interface IOfficeFormItemGroup {
  Form: string;
  GroupID: number;
  ItemGroup: string;
  ItemGroupSeq: number;
}

export interface IOfficeFormTab {
  GridForm: string;
  LoadSeq: number;
  Tab: number;
  Title: string;
  Selected?: boolean;
  IsDeletableWithHeaderForm: DeleteOptionValue;
  HasError: boolean;
  IsReadOnly: boolean;
}

export enum DeleteOptionValue {
  Yes = -1,
  No = 0,
  IgnoreThisForm = 2,
}

export interface IOfficeFormHeaderInfo {
  FormDesc: string;
  FormGroup: unknown;
  FormName: string;
  FormType: string;
  IsReadOnly: boolean;
  ModuleLongName: unknown;
  ModuleShortName: unknown;
}

export interface IHeaderSecurityData {
  Access: number;
  AttachmentAdd: YesNo;
  AttachmentDelete: YesNo;
  AttachmentUpdate: YesNo;
  RecAdd: YesNo;
  RecDelete: YesNo;
  RecUpdate: YesNo;
}

export interface IOfficeFormMetadata {
  ComboBoxData: SimpleLookupResult[];
  Fields: IOfficeField[];
  FormFieldDefaults: OfficeFieldsValuesObj;
  FormHeaderInfo: IOfficeFormHeaderInfo;
  FormItemGroups: IOfficeFormItemGroup[];
  FormTabs: IOfficeFormTab[];
  HeaderSecurityData: IHeaderSecurityData;
  comboTypes: string[];
  modalFormEvents?: OfficeToolsEvents[];
  DeleteRelatedGridRecords: boolean;
  DeleteDetailForm: boolean;
  IgnoreDeleteMessage: boolean;
  Statuses: IStatusItem[];
  RequireFormDetailsLoad: boolean;
}

export interface IOfficeData {
  FormItemMetadata: IOfficeFormMetadata;
  count: number;
  items: OfficeDataItem[];
}

export interface IOfficeGridPageSettings {
  rowsPerPage?: number | string;
  pageNumber?: number | string;
  orderby?: string[];
  search?: string[];
  filter?: string[];
}

export interface IOfficeRelatedGridParams {
  parentForm?: string;
  parentFormData?: string;
}

export type GetFormDataParams = IOfficeGridPageSettings &
  IOfficeRelatedGridParams & {
    form: string;
    co: number;
    keyid?: number;
    includeMetadata: boolean;
  };
export interface IFieldFormModeConfig {
  valLevel: number;
}

export interface ILabelDescription {
  Seq: number;
  Type: LabelTypeEnum;
}

export interface ILabelDescriptionView {
  seq: number;
  control: AbstractControl;
  type: LabelTypeEnum;
}
export interface IStatusItem {
  LabelFieldSeq: number;
  LabelFieldValue: unknown;
  Status: string;
  StatusPlaceholder: string;
}

export enum LabelTypeEnum {
  Description,
  Status,
}

export interface IFieldLabelLocalization {
  Seq: number;
  Localizations: Record<string, string>;
}
